import { constants } from '@trqk/utils';

const { PAYMENT_TYPES } = constants;

const bulkPaymentTypes: Record<string, string> = {
    '': PAYMENT_TYPES.REGULAR,
    adjustment_foreign_society: PAYMENT_TYPES.ADJUSTMENT,
    adjustment_international: PAYMENT_TYPES.ADJUSTMENT,
    adjustment_sesac: PAYMENT_TYPES.ADJUSTMENT,
    adjustment_standard: PAYMENT_TYPES.ADJUSTMENT,
    ascap_settlement_proxy: PAYMENT_TYPES.SETTLEMENT,
    ascap_settlement: PAYMENT_TYPES.SETTLEMENT,
    bmi_settlement: PAYMENT_TYPES.SETTLEMENT,
    bulk_generic: PAYMENT_TYPES.MISC_BULK,
    bulk_sample_payment: PAYMENT_TYPES.SAMPLE_SURVEY,
    cues: PAYMENT_TYPES.CUE_SHEET,
    proxy: PAYMENT_TYPES.PROXY,
    sesac_adjustment: PAYMENT_TYPES.ADJUSTMENT,
    settlements: PAYMENT_TYPES.SETTLEMENT,
};

export const getPaymentType = (isBulk: string): string =>
    bulkPaymentTypes?.[isBulk] ?? PAYMENT_TYPES.REGULAR;

/*
Mapping logic per Jeff:

ASCAP:
If the role type is “writer,” “composer name” will be empty and the “writer” would be the “statement
recipient.” We don’t know the publisher.
If the “role type” is “publisher,” then the “publisher” is “statement recipient” and “composer name”
is “writer.”
If the role type is “admin,” then “party name” is “publisher” and “composer name” is “writer.”
(Can someone have two different admin entities in one Trqk user account? If so, we might want an
“admin” column in this UI.)

SESAC:
We can't tell the difference between a writer and a publisher.

BMI:
If the statement has a "W" in the "W or P" column, we want to put the "Statement Recipient" in the
"writer" field and we don't know the publisher.
If the statement has a "P" in the "W or P" column, we want to put the "Statement Recipient" in the
"publisher" field and we don't know the writer.
*/

/**
 * Map a single RawPerformance to an item for display in the Performance Data table in the
 * Statements page.
 * @param rawPerformance a RawPerformance
 * @param {ProRoleType} entityRoleType the role_type for the currently selected entity
 * @return {PerformanceTableItem}
 */
export const mapRawPerformanceToPerformanceTableItem = (
    rawPerformance: RawPerformance,
    entityRoleType: ProRoleType,
): PerformanceTableItem => {
    const composerName = rawPerformance['Composer Name'];
    const partyName = rawPerformance['Party Name'];
    const isBulk = rawPerformance['is_bulk'];
    const pro = rawPerformance.PRO.toUpperCase();
    const statementRecipient = rawPerformance['Statement Recipient Name'];

    let publisher = '';
    let writer = '';

    if (pro === 'ASCAP') {
        if (entityRoleType === 'writer') {
            writer = statementRecipient;
        } else if (entityRoleType === 'publisher') {
            publisher = statementRecipient;
            writer = composerName;
        } else if (entityRoleType === 'admin') {
            publisher = partyName;
            writer = composerName;
        }
    } else if (pro === 'BMI') {
        if (entityRoleType === 'writer') {
            writer = statementRecipient;
        } else if (entityRoleType === 'publisher') {
            publisher = statementRecipient;
        }
    }

    return {
        ...rawPerformance,
        paymentType: getPaymentType(isBulk),
        publisher,
        writer,
    };
};
